import { useRef, useState, type ReactNode } from "react";
import { Check, Copy, Download } from "lucide-react";
import { toast } from "sonner";
import { useClipboard } from "../../../hooks/useClipboard";
import { csvFileName, tableToCsv, tableToTsv, type TableData } from "./chatMessageUtils";

const TOOLBAR_BUTTON =
  "inline-flex items-center gap-1 rounded-md px-1.5 py-0.5 text-xs text-slate-500 transition-colors hover:bg-slate-200/70 hover:text-slate-800 focus-visible:outline-2 focus-visible:outline-slate-400";

const cellText = (cell: Element): string => (cell.textContent ?? "").replace(/\s+/g, " ").trim();

export const readTable = (table: HTMLTableElement): TableData => {
  const rows = Array.from(table.rows).map((row) => Array.from(row.cells).map(cellText));
  const [columns = [], ...body] = rows;
  return { columns, rows: body };
};

export const downloadCsv = (data: TableData, title?: string) => {
  const blob = new Blob(["\uFEFF", tableToCsv(data)], { type: "text/csv;charset=utf-8" });
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = csvFileName(title);
  document.body.appendChild(link);
  link.click();
  link.remove();
  URL.revokeObjectURL(url);
};

export const AiTable = ({ title, children }: { title?: string; children: ReactNode }) => {
  const tableRef = useRef<HTMLTableElement>(null);
  const { copyToClipboard } = useClipboard();
  const [copied, setCopied] = useState(false);

  const handleCopy = async () => {
    if (!tableRef.current) return;
    const ok = await copyToClipboard(tableToTsv(readTable(tableRef.current)));
    if (!ok) {
      toast.error("Unable to copy to clipboard");
      return;
    }
    setCopied(true);
    window.setTimeout(() => setCopied(false), 1500);
  };

  const handleDownload = () => {
    if (!tableRef.current) return;
    downloadCsv(readTable(tableRef.current), title);
  };

  return (
    <div className="space-y-1">
      <div className="flex items-center justify-end gap-1">
        <button type="button" onClick={handleCopy} className={TOOLBAR_BUTTON} aria-label="Copy table" title="Copy table (paste into Excel)">
          {copied ? <Check className="size-3.5" aria-hidden="true" /> : <Copy className="size-3.5" aria-hidden="true" />}
          {copied ? "Copied" : "Copy"}
        </button>
        <button type="button" onClick={handleDownload} className={TOOLBAR_BUTTON} aria-label="Download table as CSV" title="Download CSV">
          <Download className="size-3.5" aria-hidden="true" />
          CSV
        </button>
      </div>
      <div className="overflow-x-auto">
        <table ref={tableRef} className="min-w-full border-collapse text-left text-xs">
          {children}
        </table>
      </div>
    </div>
  );
};
